// src/pages/hr/recruitment/PipelineKanban.jsx
import React, { useState } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { updateCandidateStage } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";
import CandidateProfile from "./CandidateProfile";

export default function PipelineKanban() {
  const { candidates, candidatesLoading, stages, stageColors } = useRecruitmentContext();
  const { userProfile } = useAuth();

  const [draggedCandidateId, setDraggedCandidateId] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const filteredCandidates = candidates;

  function handleDragStart(e, candidateId) {
    e.dataTransfer.setData("text/plain", candidateId);
    setDraggedCandidateId(candidateId);
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  async function handleDrop(e, targetStage) {
    e.preventDefault();
    const candidateId = e.dataTransfer.getData("text/plain") || draggedCandidateId;
    if (!candidateId) return;

    const cand = candidates.find((c) => c.id === candidateId);
    if (cand && cand.stage !== targetStage) {
      await updateCandidateStage(
        candidateId,
        targetStage,
        userProfile?.displayName || "HR Admin",
        cand.jobId
      );
    }
    setDraggedCandidateId(null);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <style>{`
        .kanban-wrapper { overflow-x: auto; padding-bottom: 1rem; }
        .kanban-board { display: flex; gap: 0.85rem; min-width: 1650px; }
        .kanban-col { flex: 1; min-width: 170px; background: var(--bg-secondary, #f8fafc); border-radius: 12px; border: 1px solid var(--border, #e2e8f0); display: flex; flex-direction: column; max-height: calc(100vh - 220px); }
        .kanban-col-header { padding: 0.75rem 0.85rem; font-weight: 700; font-size: 0.8rem; border-radius: 11px 11px 0 0; color: #fff; display: flex; justify-content: space-between; align-items: center; }
        .kanban-col-count { background: rgba(255,255,255,0.25); padding: 0.1rem 0.45rem; border-radius: 99px; font-size: 0.7rem; }
        .kanban-col-cards { padding: 0.6rem; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 0.6rem; min-height: 120px; }
        .kanban-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e2e8f0); border-radius: 10px; padding: 0.75rem 0.85rem; box-shadow: 0 1px 4px rgba(0,0,0,0.06); cursor: grab; transition: transform 0.15s, box-shadow 0.15s; }
        .kanban-card:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(0,0,0,0.1); }
        .kanban-card:active { cursor: grabbing; }
        .kanban-card-title { font-weight: 700; font-size: 0.88rem; color: var(--text-primary, #111); margin-bottom: 2px; }
        .kanban-card-sub { font-size: 0.74rem; color: var(--text-muted, #64748b); }
        .kanban-score { font-size: 0.7rem; font-weight: 700; color: #1b4b31; background: #d1fae5; padding: 0.1rem 0.4rem; border-radius: 4px; display: inline-block; margin-top: 4px; }
      `}</style>

      {/* Header Info */}
      <div style={{ display: "flex", justifyBetween: "space-between", alignItems: "center" }}>
        <div>
          <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
            💡 Drag & Drop cards between columns to advance candidate hiring stages in real-time.
          </span>
        </div>
      </div>

      {/* Kanban Container */}
      {candidatesLoading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Pipeline...</div>
      ) : (
        <div className="kanban-wrapper">
          <div className="kanban-board">
            {stages.map((stg) => {
              const stageCandidates = filteredCandidates.filter((c) => (c.stage || "Applied") === stg);
              const headerColor = stageColors[stg] || "#1b4b31";

              return (
                <div
                  key={stg}
                  className="kanban-col"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stg)}
                >
                  <div className="kanban-col-header" style={{ background: headerColor }}>
                    <span>{stg}</span>
                    <span className="kanban-col-count">{stageCandidates.length}</span>
                  </div>

                  <div className="kanban-col-cards">
                    {stageCandidates.length === 0 ? (
                      <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textStyle: "italic", textAlign: "center", padding: "1.5rem 0" }}>
                        Drop candidates here
                      </div>
                    ) : (
                      stageCandidates.map((cand) => (
                        <div
                          key={cand.id}
                          className="kanban-card"
                          draggable
                          onDragStart={(e) => handleDragStart(e, cand.id)}
                          onClick={() => setSelectedCandidate(cand)}
                        >
                          <div className="kanban-card-title">{cand.name}</div>
                          <div className="kanban-card-sub">{cand.appliedPosition || cand.role || "Applicant"}</div>
                          <div className="kanban-card-sub" style={{ fontSize: "0.7rem", marginTop: 2 }}>{cand.currentCompany || "—"}</div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                            <span className="kanban-score">AI: {cand.aiScore || 0}%</span>
                            <span style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>{cand.experience || ""}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Candidate Profile Modal */}
      {selectedCandidate && (
        <CandidateProfile
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
        />
      )}
    </div>
  );
}
